import { FormControlLabel, Radio, RadioGroup } from "@mui/material";
import { SmallBody, TinyBody } from "helpers/TypographyComponents";
import useInfiniteScroll from "hooks/useInfiniteScroll";
import type { SetStateAction } from "react";
import { useState } from "react";

import type { FilterOption } from "../FiltersAutocomplete";
import ScrollShadowWrapper from "../ScrollShadowWrapper";
import StyledPopover from "../StyledPopover";
import styles from "./style.module.scss";

/**
 * Render a simple chip as a filter
 */
interface IChipFilter {
    /**
     * Current chip filter value
     */
    value: FilterOption;
    /**
     * Options for chip filter
     */
    options: FilterOption[];
    /**
     * State that changes value to the chip filter
     */
    setValue?: React.Dispatch<SetStateAction<FilterOption>>;
    /**
     * Custom handleChoose option function
     */
    handleChooseOption?: (option: FilterOption) => void;
    label?: string;
}

const ChipFilter = ({ value, options, setValue, handleChooseOption, label }: IChipFilter) => {
    const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
    const showPopover = Boolean(anchorEl);

    const closePopover = () => setAnchorEl(null);
    const handleClickOnChip = (event: React.MouseEvent<HTMLDivElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const { paginatedData: paginatedOptions, paginatedElRef } = useInfiniteScroll(options, showPopover);

    const handleChoseOption = (e) => {
        const option = options.find((opt) => opt.id == e.target.value);
        if (option) {
            setValue && setValue(option);
            handleChooseOption && handleChooseOption(option);
        }
        closePopover();
    };

    const Icon = value?.icon;

    return (
        <div className={styles["chip-filter"]}>
            {label && <label>{label}</label>}
            <div className={styles["chip"]} onClick={handleClickOnChip} onMouseDown={(e) => e.stopPropagation()}>
                <label>
                    {Icon && <Icon />} {value?.name}
                </label>
            </div>
            <StyledPopover open={showPopover} onClose={closePopover} anchorEl={anchorEl} multicolor>
                <ScrollShadowWrapper className="chip-filter-list-scroller" ref={paginatedElRef}>
                    <div className={styles["option-wrapper"]}>
                        <RadioGroup onMouseDown={(e) => e.stopPropagation()} onChange={(e) => handleChoseOption(e)}>
                            {paginatedOptions.map((option) => {
                                const checked = value?.id === option?.id;
                                return (
                                    <FormControlLabel
                                        className={styles["option"]}
                                        value={option?.id}
                                        checked={checked}
                                        control={<Radio />}
                                        label={<LabelOption option={option} />}
                                        key={`chip-option-${option?.id}`}
                                    />
                                );
                            })}
                        </RadioGroup>
                    </div>
                </ScrollShadowWrapper>
            </StyledPopover>
        </div>
    );
};

export default ChipFilter;

/**
 * Render ChipFilter label option
 */
const LabelOption = ({ option }: { option: FilterOption }) => (
    <div>
        <div className={styles["option-label"]}>
            <SmallBody>{option?.name}</SmallBody>
        </div>
        {option?.description && <TinyBody color="grey">{option.description}</TinyBody>}
    </div>
);
